<template>
  <v-card class="rsl-fixed-height">
    <v-card-title>
      {{ tableName }}
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        :label="$t('DB_TABLES.VIEW_TABLE.SEARCH_TEXT')"
        single-line
        hide-details
      ></v-text-field>
      <v-spacer></v-spacer>
      <v-btn
        small
        class="mr-2"
        color="success"
        @click="newRow()"
      >{{ $t('DB_TABLES.BTNS.ADD_ROW') }}</v-btn>
      <v-btn
        small
        class="mr-2"
        color="secondary"
        @click="close()"
      >{{ $t('GENERAL.BTNS.CLOSE') }}</v-btn>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="tableData"
      :page.sync="page"
      :items-per-page="itemsPerPage"
      hide-default-footer
      :search="search"
      class="elevation-1 mx-3 mb-3"
      item-key="ID"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      loading-text="Loading... Please wait"
      :loading="loading"
      fixed-header
      @page-count="pageCount = $event"
    >
      <template slot="item.actions" slot-scope="row">
        <v-btn
          small
          class="mr-2"
          color="warning"
          @click="editRow(row.item)"
        >
          {{ $t('GENERAL.BTNS.EDIT') }}
        </v-btn>
        <v-btn
          small
          class="mr-2"
          color="error"
          @click="deleteRow(row.item)"
        >
          {{ $t('GENERAL.BTNS.DELETE') }}
        </v-btn>
      </template>
    </v-data-table>
    <div class="text-center pt-2 mx-2">
      <v-btn
        small
        class="mr-2 float-left white--text"
        color="#f57c00"
        :disabled="offset == 0"
        @click="prevBatch()"
      >{{ $t('DB_TABLES.BTNS.FETCH_PREV') }} {{ pageSize }}</v-btn>
      <v-btn
        small
        class="mr-2 float-right white--text"
        color="#f57c00"
        :disabled="orderDir == 'ASC' ? rowTo >= tableRows : rowFrom <= pageSize"
        @click="nextBatch()"
      >{{ $t('DB_TABLES.BTNS.FETCH_NEXT') }} {{ pageSize }}</v-btn>
      <p v-if="orderDir == 'ASC'">Showing rows {{ rowFrom }} to {{ rowTo }} of {{ tableRows }} </p>
      <p v-if="orderDir == 'DESC'">Showing rows {{ rowFrom }} to {{ rowTo }} of {{ tableRows }} </p>
      <v-pagination
        v-model="page"
        :length="pageCount"
      ></v-pagination>
    </div>
    <div class="text-center pt-2 mx-2">
      <v-row>
        <v-col cols="12" sm="3">
          <v-select
            v-model="orderField"
            :label="$t('DB_TABLES.VIEW_TABLE.SORT_LABEL')"
            :items="tableHeaders"
            item-text="fieldName"
            item-value="fieldName"
            @change="updateOrderBy"
          ></v-select>
        </v-col>
        <v-col sm="3">
          <v-select
            v-model="orderDir"
            :label="$t('DB_TABLES.VIEW_TABLE.DIR_LABEL')"
            :items="orderDirs"
            item-text="text"
            item-value="val"
            @change="updateOrderBy"
          ></v-select>
        </v-col>
        <v-col sm="3">
          <v-text-field
            :value="pageSize"
            label="Returned records (max 5000)"
            type="number"
            min="1"
            max="5000"
            class="mt-6"
            @change="updateResultset($event)"
          ></v-text-field>
        </v-col>
        <v-col sm="3">
          <v-text-field
            :value="itemsPerPage"
            label="Items per page (max 1000)"
            type="number"
            min="1"
            max="1000"
            class="mt-6"
            @input="itemsPerPage = parseInt($event, 10)"
          ></v-text-field>
        </v-col>
      </v-row>
    </div>

    <v-dialog
      transition="dialog-bottom-transition"
      max-width="500"
      v-model="addRowDialog"
      :retain-focus="false"
      persistent
    >
      <template>
        <v-card>
          <v-toolbar
            color="#f87723"
            dark
          >{{ addRowTitle }}</v-toolbar>
          <v-card-text>
            <div class="pt-5">{{ addRowText }}</div>
            <v-form ref="addRowForm" v-model="isFormValid">
            <div v-for="item in tableHeaders" :key="item.fieldName">
              <div v-if="item.fieldName != 'ID'">
                <v-text-field
                  v-if="item.fieldType == 'Varchar'"
                  :data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :rules="!item.allowNull ? [rules.required] : []"
                ></v-text-field>
                <v-text-field
                  v-if="item.fieldType == 'Integer' || item.fieldType == 'Big Integer'"
                  type="number"
                  step="1"
                  :data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :rules="!item.allowNull ? [rules.required] : []"
                ></v-text-field>
                <v-text-field
                  v-if="item.fieldType == 'Decimal'"
                  type="number"
                  step="any"
                  :data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required] : []"
                ></v-text-field>
                <v-textarea 
                  v-if="item.fieldType == 'Text' || item.fieldType == 'Longtext' || item.fieldType == 'Blob'"
                  :data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :rules="!item.allowNull ? [rules.required] : []"
                  rows="3">
                </v-textarea>
                <v-checkbox
                  v-if="item.fieldType == 'Boolean'"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :data-field="item.fieldName"
                  :rules="!item.allowNull ? [rules.required] : []"
                ></v-checkbox>
                <v-text-field
                  v-if="item.fieldType == 'Date'"
                  :data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :rules="!item.allowNull ? [rules.required, rules.dateFormat] : [rules.dateFormat]"
                ></v-text-field>
                <v-text-field
                  v-if="item.fieldType == 'Time'"
                  :data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :rules="!item.allowNull ? [rules.required, rules.timeFormat] : [rules.timeFormat]"
                ></v-text-field>
                <v-text-field
                  v-if="item.fieldType == 'DateTime'"
                  :data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :rules="!item.allowNull ? [rules.required, rules.dateTimeFormat] : [rules.dateTimeFormat]"
                ></v-text-field>
              </div>
            </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="cancelAddRow()"
            >{{ $t('GENERAL.BTNS.CANCEL') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              :disabled="!isFormValid"
              @click="confirmAddRow()"
            >{{ $t('GENERAL.BTNS.ADD') }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <v-dialog
      transition="dialog-bottom-transition"
      max-width="500"
      v-model="editRowDialog"
      :retain-focus="false"
      persistent
    >
      <template>
        <v-card>
          <v-toolbar
            color="#f87723"
            dark
          >{{ editRowTitle }}</v-toolbar>
          <v-card-text>
            <div class="pt-5">{{ editRowText }}</div>
            <v-form ref="editRowForm" v-model="isFormValid">
            <div v-for="item in tableHeaders" :key="item.fieldName">
              <div v-if="item.fieldName != 'ID'">
                <v-text-field
                  v-if="item.fieldType == 'Varchar'"
                  :edit-data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required] : []"
                ></v-text-field>
                <v-text-field
                  v-if="item.fieldType == 'Integer' || item.fieldType == 'Big Integer'"
                  type="number"
                  step="1"
                  :edit-data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required] : []"
                ></v-text-field>
                <v-text-field
                  v-if="item.fieldType == 'Decimal'"
                  type="number"
                  step="any"
                  :edit-data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required] : []"
                ></v-text-field>
                <v-textarea 
                  v-if="item.fieldType == 'Text' || item.fieldType == 'Longtext' || item.fieldType == 'Blob'"
                  :edit-data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required] : []"
                  rows="3">
                </v-textarea>
                <v-checkbox
                  v-if="item.fieldType == 'Boolean'"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :edit-data-field="item.fieldName"
                  :value="rowData[item.fieldName]"
                  :input-value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required] : []"
                ></v-checkbox>
                <v-text-field
                  v-if="item.fieldType == 'Date'"
                  :edit-data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required, rules.dateFormat] : [rules.dateFormat]"
                ></v-text-field>
                <v-text-field
                  v-if="item.fieldType == 'Time'"
                  :edit-data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required, rules.timeFormat] : [rules.timeFormat]"
                ></v-text-field>
                <v-text-field
                  v-if="item.fieldType == 'DateTime'"
                  :edit-data-field="item.fieldName"
                  :label="item.fieldName + ' (' + item.fieldType + ')'"
                  :value="rowData[item.fieldName]"
                  :rules="!item.allowNull ? [rules.required, rules.dateTimeFormat] : [rules.dateTimeFormat]"
                ></v-text-field>
              </div>
            </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="cancelEditRow()"
            >{{ $t('GENERAL.BTNS.CANCEL') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              :disabled="!isFormValid"
              @click="confirmEditRow()"
            >{{ $t('GENERAL.BTNS.SAVE') }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <v-dialog
      transition="dialog-bottom-transition"
      max-width="400"
      v-model="deleteRowDialog"
      :retain-focus="false"
      persistent
    >
      <template>
        <v-card>
          <v-toolbar
            color="#f87723"
            dark
          >{{ deleteRowTitle }}</v-toolbar>
          <v-card-text>
            <div class="pt-5">{{ deleteRowText }}</div>
            <div>{{ deleteRowText2 }}</div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="deleteRowDialog = false"
            >{{ $t('GENERAL.BTNS.CANCEL') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="confirmDeleteRow()"
            >{{ $t('GENERAL.BTNS.CONFIRM') }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-card>
</template>

<script>
  import $ from 'jquery';
  import { mapGetters } from 'vuex';
  export default {
    name: 'viewtable',
    computed: {
      ...mapGetters(['tableId', 'tableName', 'tableHeaders', 'tableData', 'tablePages', 'tableRows'])
    },
    data: function() {
      return {
        loading: false,
        offset: 0,
        pageSize: 1000,
        page: 1,
        pageCount: 0,
        itemsPerPage: 50,
        rowTo: 1,
        rowFrom: 1,
        orderBy: 'ID DESC',
        orderField: 'ID',
        orderDir: 'DESC',
        deleteRowDialog: false,
        deleteRowTitle: '',
        deleteRowText: '',
        deleteRowText2: '',
        deleteRowId: '',
        addRowDialog: false,
        addRowTitle: '',
        addRowText: '',
        editRowDialog: false,
        editRowTitle: '',
        editRowText: '',
        rowData: [],
        rowId: null,
        search: '',
        headers: [],
        newRowData: [],
        editRowData: [],
        sortBy: '',
        sortDesc: false,
        isFormValid: false,
        orderDirs: [
          { text: this.$t('DB_TABLES.VIEW_TABLE.ASC_LABEL'), val: 'ASC'},
          { text: this.$t('DB_TABLES.VIEW_TABLE.DESC_LABEL'), val: 'DESC'}
        ],
        rules: {
          required: value => !!value || 'Required',
          dateFormat: value => {
            const pattern = /^\d{4}-\d{2}-\d{2}$/
            if(!value) {
              return true
            }
            return pattern.test(value) || 'Invalid date format. Please use YYYY-MM-DD format.'
          },
          timeFormat: value => {
            const pattern = /^\d{2}:\d{2}:\d{2}$/
            if(!value) {
              return true
            }
            return pattern.test(value) || 'Invalid time format. Please use HH:MM:SS format.'
          },
          dateTimeFormat: value => {
            const pattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/
            if(!value) {
              return true
            }
            return pattern.test(value) || 'Invalid date format. Please use YYYY-MM-DD HH:MM:SS format.'
          }
        }
      }
    },
    created() {
      this.unsubscribe = this.$store.subscribe((mutation) => {

        if (mutation.type === 'DELETED_ROW' || mutation.type === 'ADDED_ROW' || mutation.type === 'EDITED_ROW') {
          this.refreshData();
        }

      });

      // Set the headers
      for(let i = 0; i < this.tableHeaders.length; i++) {
        if(i == 0) {
          this.headers.push({ text: this.tableHeaders[i].fieldName, value: this.tableHeaders[i].fieldName, align: 'start', sortable: true, width: 100 });
        }else {
          this.headers.push({ text: this.tableHeaders[i].fieldName, value: this.tableHeaders[i].fieldName, sortable: true });
        }
      }
      // Add the actions
      this.headers.push({ text: this.$t('DB_TABLES.FIELDS.ACTIONS'), value: 'actions', sortable: false, width: 200 });
      if(this.orderDir == 'DESC') {
        this.rowTo = this.tableRows;
        this.rowFrom = this.tableRows - this.pageSize + 1;
        this.$store.commit('setViewTableSortOrder', 'descending');
      }else {
        this.$store.commit('setViewTableSortOrder', 'ascending');
      }
      this.$store.commit('setViewTableSortField', this.orderField);
      this.refreshData();
    },
    beforeDestroy() {
      this.unsubscribe();
    },
    methods: {
      refreshData() {
        this.loading = true;
        let payload = {
          'tableId': this.tableId,
          'offset': this.offset,
          'orderBy': this.orderBy,
          'pageSize': this.pageSize
        }
        this.$store.dispatch('viewPaginatedTable', payload).then( () => {
          this.loading = false;

          if(this.orderDir == 'ASC') {
            if(this.rowFrom + this.pageSize > this.tableRows) {
              this.rowTo = this.tableRows;
            }
          }
          if(this.orderDir == 'DESC') {
            if(this.rowTo - this.pageSize < 1) {
              if(this.tableRows <= this.pageSize) {
                this.rowTo = this.tableRows;
              }else {
                this.rowTo = this.pageSize + 1;
              }
              this.rowFrom = 1;
            }
          }
          // Apply sort field and order
          let field = this.$store.state.viewTableSortField;
          let sort = this.$store.state.viewTableSortOrder;
          
          if(field == null) {
            this.sortBy = 'ID';
            this.sortDesc = true;
          }else if (sort == 'none') {
            this.sortBy = '';
            this.sortDesc = false;
          }else {
            this.sortBy = field;

            if(sort == 'descending') {
              this.sortDesc = true;
            }else {
              this.sortDesc = false;
            }
          }
          // Remove previous click events
          $('.text-start.sortable').off();
          // Add new click events for storing order
          let ref = this;
          $('.text-start.sortable').click(function() {
            let column = $(this)[0].firstChild.innerHTML;
            let order = $(this).attr('aria-sort');
            
            for(let i = 0; i < ref.headers.length; i++) {
              if(column == ref.headers[i].text) {
                ref.$store.commit('setViewTableSortField', ref.headers[i].value);
                ref.$store.commit('setViewTableSortOrder', order);
              }
            }
          });
        });
      },
      prevBatch() {
        if(this.offset > 0) {
          this.offset -= this.pageSize;
          if(this.orderDir == 'ASC') {
            this.rowFrom -= this.pageSize;
            this.rowTo -= this.pageSize;
          }else {
            this.rowFrom += this.pageSize;
            this.rowTo += this.pageSize;
          }
        }
        this.refreshData();
      },
      nextBatch() {
        if(this.offset < this.tableRows) {
          this.offset += this.pageSize;
          if(this.orderDir == 'ASC') {
            this.rowFrom += this.pageSize;
            this.rowTo += this.pageSize;
          }else {
            this.rowFrom -= this.pageSize;
            this.rowTo -= this.pageSize;
          }
        }
        this.refreshData();
      },
      updateOrderBy() {
        this.offset = 0;
        this.orderBy = this.orderField + ' ' + this.orderDir;
        let order = 'ascending';

        if(this.orderDir == 'DESC') {
          order = 'descending';
        }
        this.$store.commit('setViewTableSortField', this.orderField);
        this.$store.commit('setViewTableSortOrder', order);
        this.refreshData();
        this.correctRows();
      },
      correctRows() {
        if(this.orderDir == 'ASC') {
          this.rowFrom = 1;
          this.rowTo = this.rowFrom + this.pageSize - 1;
        }else {
          this.rowTo = this.tableRows;
          this.rowFrom = this.tableRows - this.pageSize + 1;
        }
      },
      updateResultset(pageSize) {
        this.pageSize = parseInt(pageSize, 10);
        this.refreshData();
        this.correctRows();
      },
      newRow() {        
        this.addRowTitle = this.$t('DB_TABLES.VIEW_TABLE.ADD_ROW.TITLE');
        this.addRowText = this.$t('DB_TABLES.VIEW_TABLE.ADD_ROW.DESC');
        this.addRowDialog = true;
      },
      cancelAddRow() {
        this.$refs.addRowForm.reset();
        this.$refs.addRowForm.resetValidation();
        this.addRowDialog = false;
      },
      confirmAddRow() {
        for(let i = 0; i < this.tableHeaders.length; i++) {
          if(this.tableHeaders[i].fieldName != 'ID') {
            let target = document.querySelector('[data-field="' + this.tableHeaders[i].fieldName + '"]');
            
            if(target.type == 'checkbox') {
              if(target.checked) {
                this.newRowData.push({ fieldName: this.tableHeaders[i].fieldName, fieldType: this.tableHeaders[i].fieldType, fieldValue: 1 });
              }else {
                this.newRowData.push({ fieldName: this.tableHeaders[i].fieldName, fieldType: this.tableHeaders[i].fieldType, fieldValue: 0 });
              }
            }else {
              if(!target.value) {
                this.newRowData.push({ fieldName: this.tableHeaders[i].fieldName, fieldType: this.tableHeaders[i].fieldType, fieldValue: 'NULL' });
              }else {
                this.newRowData.push({ fieldName: this.tableHeaders[i].fieldName, fieldType: this.tableHeaders[i].fieldType, fieldValue: target.value });
              }
            }
          }
        }

        this.addRowDialog = false;
        let payload = {
          'tableName': this.tableName,
          'rowData': JSON.stringify(this.newRowData)
        };
        this.$store.dispatch('addTableRow', payload).then( () => {
          this.updateLog('Database', 'Row Added', 'Row added to table ' + this.tableName + '. Row: ' + JSON.stringify(this.newRowData));
          this.newRowData = [];
          this.$refs.addRowForm.reset();
          this.$refs.addRowForm.resetValidation();
        });
      },
      close() {
        this.$router.push({ name: 'DatabaseTables' });
      },
      editRow(item) {
        this.editRowTitle = this.$t('DB_TABLES.VIEW_TABLE.EDIT_ROW.TITLE');
        this.editRowText = this.$t('DB_TABLES.VIEW_TABLE.EDIT_ROW.DESC');
        this.rowData = JSON.parse(JSON.stringify(item));
        this.rowId = item.ID;
        this.editRowDialog = true;
      },
      cancelEditRow() {
        this.$refs.editRowForm.reset();
        this.$refs.editRowForm.resetValidation();
        this.editRowDialog = false;
        this.rowData = [];
      },
      confirmEditRow() {
        for(let i = 0; i < this.tableHeaders.length; i++) {
          if(this.tableHeaders[i].fieldName != 'ID') {
            let target = document.querySelector('[edit-data-field="' + this.tableHeaders[i].fieldName + '"]');
            
            if(target.type == 'checkbox') {
              if(target.checked) {
                this.editRowData.push({ fieldName: this.tableHeaders[i].fieldName, fieldType: this.tableHeaders[i].fieldType, fieldValue: 1 });
              }else {
                this.editRowData.push({ fieldName: this.tableHeaders[i].fieldName, fieldType: this.tableHeaders[i].fieldType, fieldValue: 0 });
              }
            }else {
              if(!target.value) {
                this.editRowData.push({ fieldName: this.tableHeaders[i].fieldName, fieldType: this.tableHeaders[i].fieldType, fieldValue: 'NULL' });
              }else {
                this.editRowData.push({ fieldName: this.tableHeaders[i].fieldName, fieldType: this.tableHeaders[i].fieldType, fieldValue: target.value });
              }
            }
          }
        }

        this.editRowDialog = false;
        let payload = {
          'tableName': this.tableName,
          'rowData': JSON.stringify(this.editRowData),
          'rowId': this.rowId
        };
        this.$store.dispatch('editTableRow', payload).then( () => {
          this.updateLog('Database', 'Row Edited', 'Row edited in table ' + this.tableName + '. Previous state: ' + JSON.stringify(this.rowData) + ' ---> Current state: ' + JSON.stringify(this.editRowData));
          this.rowData = [];
          this.$refs.editRowForm.reset();
          this.$refs.editRowForm.resetValidation();
        });
      },
      deleteRow(item) {
        this.deleteRowTitle = this.$t('DB_TABLES.VIEW_TABLE.DELETE_ROW.TITLE');
        this.deleteRowText = this.$t('DB_TABLES.VIEW_TABLE.DELETE_ROW.DESC');
        this.deleteRowText2 = this.$t('DB_TABLES.VIEW_TABLE.DELETE_ROW.ROW') + ' ' + item.ID;
        this.deleteRowId = item.ID;
        this.rowData = item;
        this.deleteRowDialog = true;
      },
      confirmDeleteRow() {
        this.deleteRowDialog = false;
        let payload = {
          'tableName': this.tableName,
          'rowId': this.deleteRowId
        };
        this.$store.dispatch('deleteTableRow', payload).then( () => {
          this.updateLog('Database', 'Row Deleted', 'Row deleted from table ' + this.tableName + '. Deleted row: ' + JSON.stringify(this.rowData));
          this.rowData = [];
        });
      },
      updateLog(cat, op, msg) {
        let dt = new Date();
        let dateTime = new Intl.DateTimeFormat('en-GB', 
          {
            day: 'numeric',
            month: 'numeric', 
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false
          }).format(dt);

        let logData = {
          'category': cat,
          'userId': this.$store.state.currentUser,
          'caseId': 0,
          'processName': null,
          'taskName': null,
          'operation': op,
          'msg': msg,
          'dateTime': dateTime,
        };
        this.$store.dispatch('updateOpLog', logData);
      }
    }
  }
</script>
<style>
  .rsl-fixed-height {
    max-width: calc(100vw - 275px);
  }
</style>